import { CalendarIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import React from "react";

import DataTable from "../../shared/components/DataTable/DataTable";
import useQueryDataTable from "../../shared/components/DataTable/useQueryDataTable";
import RangeDatePicker from "../../shared/components/DatePicker/RangeDatePicker";
import AgencyMemberSelect from "../../shared/components/AgencyMemberSelect";
import ContractTypeSelect from "../../shared/components/ContractTypeSelect";
import PatientStatusSelect from "../../shared/components/PatientStatusSelect";
import { createFilters } from "../../shared/hooks/useFilters";
import useApi from "../../shared/hooks/useApi";
import { useEntityLink } from "../../shared/hooks/useEntityLink";
import { AuthorizationId, PatientId } from "../../shared/schema/schema";
import AiWeeklyTemplateBuilderModal from "../ai-weekly-template-builder/components/AiWeeklyTemplateBuilderModal";
import { EntitySelect } from "../workflow/components/EntityFormControl";
import {
  AuthorizationUtilizationState,
  PatientUtilizationList,
  PatientUtilizationListItem,
} from "./authorization-utilization.types";
import {
  AuthorizedHours,
  AuthTypeSelect,
  PatientStatusBadge,
  UtilizationBar,
  UtilizationRemaining,
  UtilizationStateBadge,
  UtilizationStateSelect,
} from "./authorization-utilization.utils";
import {
  SharedUtilizationFilters,
  useSharedUtilizationFilters,
} from "./useSharedUtilizationFilters";

// State/search are specific to the list; date range + office + contract are
// shared with the Dashboard tab (see useSharedUtilizationFilters).
type ListOnlyFilters = {
  states?: AuthorizationUtilizationState[];
  search?: string;
  noScheduleYet?: boolean;
};

const { createRangeDatePickerFilter } = createFilters<SharedUtilizationFilters>();

export default function PatientUtilizationListTab() {
  const [shared, setShared] = useSharedUtilizationFilters();
  const [listFilters, setListFilters] = React.useState<ListOnlyFilters>({});
  // The row the weekly-template assistant is open for. Holds the authorization
  // too, so the modal opens on the one this row is about rather than making the
  // coordinator pick it again.
  const [builderFor, setBuilderFor] = React.useState<{
    patientId: PatientId;
    authorizationId: AuthorizationId;
  } | null>(null);
  const { queries } = useApi();
  const entityLink = useEntityLink();

  // Memoized so the queryKey reference is stable across renders; otherwise
  // usePageFetchingObserver's effect re-fires every render and the page
  // loading indicator never settles.
  const queryOptions = React.useMemo(
    () =>
      queries.authorizationUtilization.patients({
        from: shared.from,
        to: shared.to,
        officeIds: shared.officeIds,
        contractTypeIds: shared.contractTypeIds,
        coordinatorIds: shared.coordinatorIds,
        periodTypes: shared.periodTypes,
        patientStatuses: shared.patientStatuses,
        states: listFilters.states,
        noScheduleYet: listFilters.noScheduleYet,
        search:
          listFilters.search && listFilters.search.length > 0 ? listFilters.search : undefined,
        // Client-side pagination in the table; fetch a generous page.
        limit: 5000,
        offset: 0,
      }),
    [
      queries,
      shared.from,
      shared.to,
      shared.officeIds,
      shared.contractTypeIds,
      shared.coordinatorIds,
      shared.periodTypes,
      shared.patientStatuses,
      listFilters.states,
      listFilters.noScheduleYet,
      listFilters.search,
    ]
  );

  // Auto-load only once the user has chosen a real filter (not just the default
  // date range) — avoids loading the whole agency on first open.
  const hasFilters =
    (shared.officeIds?.length ?? 0) > 0 ||
    (shared.contractTypeIds?.length ?? 0) > 0 ||
    (shared.coordinatorIds?.length ?? 0) > 0 ||
    (shared.periodTypes?.length ?? 0) > 0 ||
    (shared.patientStatuses?.length ?? 0) > 0 ||
    (listFilters.states?.length ?? 0) > 0 ||
    (listFilters.search?.length ?? 0) > 0 ||
    listFilters.noScheduleYet === true;

  // The DataTable shows its own loading state (via useQueryDataTable).
  const query = useQuery({
    ...queryOptions,
    select: (data: PatientUtilizationList) => data.nodes,
    enabled: hasFilters,
  });

  const fromToFilter = createRangeDatePickerFilter({
    label: "Date range",
    description: "Shows authorizations whose period overlaps the selected date range",
    startDate: { name: "from", value: shared.from ? LocalDate.parse(shared.from) : null },
    endDate: { name: "to", value: shared.to ? LocalDate.parse(shared.to) : null },
    onChange: (key, value) => setShared((prev) => ({ ...prev, [key]: value?.toString() })),
  });

  // setBuilderFor is stable, so the columns never need rebuilding.
  const columns = React.useMemo(() => createColumns(setBuilderFor), []);

  const { dataTableProps } = useQueryDataTable({
    query,
    columns,
    columnVisiblity: {},
    initialSorting: [{ id: "unscheduledMinutes", desc: true }],
    trProps: (row) => ({
      cursor: "pointer",
      // Open the patient the same way the "Clocked visits (new)" page does (an
      // overlay via the React<->Angular bridge), so this worklist is preserved.
      // Cmd/Ctrl-click opens in a new tab.
      onClick: (e) =>
        entityLink.open({ event: e, entity: { type: "Patient", id: row.patientId as PatientId } }),
    }),
  });

  const filterNode = (
    <>
      <Tooltip hasArrow label={fromToFilter.description} placement="bottom-start">
        <InputGroup width="xs">
          <RangeDatePicker {...fromToFilter} inputProps={{ width: "full" }} />
          <InputRightElement>
            <CalendarIcon color="gray.400" />
          </InputRightElement>
        </InputGroup>
      </Tooltip>

      <EntitySelect
        input={{ type: "entity", entity: "Office" }}
        label="Office"
        multiple={true}
        renderUnselected="Office"
        value={shared.officeIds ?? null}
        onChange={(x) => setShared((prev) => ({ ...prev, officeIds: x ?? undefined }))}
      />

      <ContractTypeSelect
        label="Contract"
        multiple={true}
        value={shared.contractTypeIds ?? null}
        onChange={(x) => setShared((prev) => ({ ...prev, contractTypeIds: x ?? undefined }))}
      />

      <AuthTypeSelect
        value={shared.periodTypes ?? null}
        onChange={(x) => setShared((prev) => ({ ...prev, periodTypes: x ?? undefined }))}
      />

      <AgencyMemberSelect
        label="Coordinator"
        multiple={true}
        value={shared.coordinatorIds ?? null}
        onChange={(x) => setShared((prev) => ({ ...prev, coordinatorIds: x ?? undefined }))}
      />

      <PatientStatusSelect
        label="Patient status"
        multiple={true}
        value={shared.patientStatuses ?? null}
        onChange={(x) => setShared((prev) => ({ ...prev, patientStatuses: x ?? undefined }))}
      />

      <UtilizationStateSelect
        value={listFilters.states ?? null}
        onChange={(x) => setListFilters((prev) => ({ ...prev, states: x ?? undefined }))}
      />

      <Tooltip
        hasArrow
        label="Patients with no future visits on the calendar"
        placement="bottom-start"
      >
        <Checkbox
          isChecked={listFilters.noScheduleYet ?? false}
          onChange={(e) =>
            setListFilters((prev) => ({
              ...prev,
              noScheduleYet: e.target.checked ? true : undefined,
            }))
          }
        >
          <Text fontSize="sm">Not scheduled yet</Text>
        </Checkbox>
      </Tooltip>

      <InputGroup width="2xs">
        <Input
          placeholder="Search patient"
          value={listFilters.search ?? ""}
          onChange={(e) => setListFilters((prev) => ({ ...prev, search: e.target.value }))}
        />
        <InputRightElement>
          <SearchIcon color="gray.400" />
        </InputRightElement>
      </InputGroup>
    </>
  );

  return (
    <Box>
      <DataTable {...dataTableProps} filterNode={filterNode} spacing="tight" />

      {builderFor !== null && (
        <AiWeeklyTemplateBuilderModal
          initialAuthorizationId={builderFor.authorizationId}
          patientId={builderFor.patientId}
          onClose={() => setBuilderFor(null)}
        />
      )}
    </Box>
  );
}

const { accessor, display } = createColumnHelper<PatientUtilizationListItem>();

const createColumns = (
  onSuggestTemplate: (row: { patientId: PatientId; authorizationId: AuthorizationId }) => void
) => [
  accessor("patientName", {
    header: "Patient",
    sortingFn: "alphanumeric",
    cell: (x) => (
      <Box>
        <Text>{x.row.original.patientName}</Text>
        <Text color="gray.500" fontSize="xs">
          {x.row.original.periodType} · {x.row.original.officeName}
        </Text>
      </Box>
    ),
  }),
  accessor("contractTypeName", {
    header: "Contract",
    sortingFn: "alphanumeric",
    cell: (x) => (
      <Box>
        <Text>{x.row.original.contractTypeName}</Text>
        <Text color="gray.500" fontSize="xs">
          {x.row.original.serviceCode}
        </Text>
      </Box>
    ),
  }),
  accessor("patientStatus", {
    header: "Patient status",
    sortingFn: "alphanumeric",
    cell: (x) => <PatientStatusBadge status={x.row.original.patientStatus} />,
  }),
  accessor("authStartDate", {
    id: "authDates",
    header: "Auth dates",
    sortingFn: "alphanumeric",
    cell: (x) => (
      <Text fontSize="sm" whiteSpace="nowrap">
        {x.row.original.authStartDate} – {x.row.original.authEndDate}
      </Text>
    ),
  }),
  accessor((row) => row.utilization.authorizedMinutes ?? -1, {
    id: "authorizedMinutes",
    header: "Authorized (h)",
    sortingFn: "basic",
    cell: (x) => (
      <AuthorizedHours
        authorizedMinutes={x.row.original.utilization.authorizedMinutes}
        authorizedReductionMinutes={x.row.original.utilization.authorizedReductionMinutes}
        unavailableDays={x.row.original.utilization.unavailableDays}
      />
    ),
  }),
  accessor((row) => row.utilization.scheduledUtilization ?? -1, {
    id: "scheduledUtilization",
    header: "Scheduled",
    sortingFn: "basic",
    cell: (x) => (
      <UtilizationBar
        authorizedMinutes={x.row.original.utilization.authorizedMinutes}
        scheduledMinutes={x.row.original.utilization.scheduledMinutes}
        state={x.row.original.utilization.state}
      />
    ),
  }),
  accessor(
    (row) =>
      row.utilization.authorizedMinutes === null
        ? Number.MAX_SAFE_INTEGER
        : row.utilization.authorizedMinutes - row.utilization.scheduledMinutes,
    {
      id: "unscheduledMinutes",
      header: "Unscheduled (h)",
      sortingFn: "basic",
      cell: (x) => (
        <UtilizationRemaining
          authorizedMinutes={x.row.original.utilization.authorizedMinutes}
          scheduledMinutes={x.row.original.utilization.scheduledMinutes}
        />
      ),
    }
  ),
  display({
    id: "state",
    header: "State",
    cell: (x) => <UtilizationStateBadge state={x.row.original.utilization.state} />,
  }),
  display({
    id: "actions",
    header: "",
    cell: (x) => {
      // Only offered where it can actually do something: the builder creates a
      // schedule from nothing, so it makes no sense for an authorization that
      // already has visits, nor for a patient who is not active.
      if (x.row.original.patientStatus !== "ACTIVE" || x.row.original.hasActiveVisits) {
        return null;
      }

      return (
        <Button
          size="sm"
          variant="outline"
          whiteSpace="nowrap"
          // The whole row has an onClick that opens the patient overlay (see
          // trProps); without stopping propagation the overlay swallows the modal.
          onClick={(e) => {
            e.stopPropagation();
            onSuggestTemplate({
              patientId: x.row.original.patientId as PatientId,
              authorizationId: x.row.original.authorizationId as AuthorizationId,
            });
          }}
        >
          Suggest weekly template
        </Button>
      );
    },
  }),
];
